import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaArrowLeft,
  FaHeart,
  FaRegHeart,
  FaUserCheck,
  FaMapMarkerAlt,
  FaBriefcase,
  FaGraduationCap,
  FaRulerVertical,
  FaPrayingHands,
  FaLanguage,
  FaCamera,
  FaPhoneAlt,
  FaEnvelope,
  FaLock,
  FaBirthdayCake,
  FaWeight,
  FaVenusMars,
  FaHome,
  FaUsers,
  FaStar,
  FaIdCard,
  FaMoneyBillWave,
  FaUniversity,
  FaCity,
  FaGlobe,
  FaInfoCircle,
  FaCalendarAlt,
  FaTint,
  FaSmoking,
  FaWineGlassAlt,
  FaUtensils,
  FaTimes,
  FaSuitcase,
  FaUserFriends,
  FaShieldAlt,
  FaEye,
  FaCheckCircle,
} from "react-icons/fa";
import { motion } from "framer-motion";
import api from "../../services/api";

const ProfileDetails = ({ profileId, onBack, onOpenConversation, onUpgradeClick }) => {
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [limitError, setLimitError] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);



  const [contactError, setContactError] = useState("");
  const BASE_URL = api.defaults.baseURL;
  const baseUrl = BASE_URL.replace("/api", "");

  const profileRef = useRef(null);

  const checkIfShortlisted = async () => {
    try {
      const response = await api.get("/shortlisted");
      const shortlistedIds = response.data.map((p) => p._id);
      setIsShortlisted(shortlistedIds.includes(profile._id));
    } catch (error) {
      console.error("Error checking shortlist:", error);
    }
  };
  useEffect(() => {
    if (!profileId) return;

    const fetchProfileById = async () => {
      setLoadingProfile(true);
      try {
        const res = await api.get(`/profile/${profileId}`);
        const fetchedProfile = res.data.profile || res.data;
        setProfile(fetchedProfile);
        setUserPlan(res.data.currentPlan);
        setPlanFeatures(res.data.planFeatures || {});

        await api.post("/record-visit", { visitedProfileId: fetchedProfile._id || fetchedProfile.id });
      } catch (error) {
        const data = error.response?.data;

        if (error.response?.status === 403 && data?.limitExceeded) {
          setLimitError(data); // ⬅️ STORE BACKEND MESSAGE
          return;
        }

        console.error("Failed to fetch profile", error);
        onBack();
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfileById();
  }, [profileId]);

  useEffect(() => {
    if (profile && profileRef.current) {
      profileRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      checkIfShortlisted();
      fetchUserPlan();
    }
  }, [profile]);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get("/me");
      setUserPlan(response.data.subscriptionPlan);
    } catch (error) {
      console.error("Error fetching user plan:", error);
    }
  };

  const recordVisit = async () => {
    try {
      await api.post("/record-visit", { visitedProfileId: profile._id });
    } catch (error) {
      console.error("Error recording visit:", error);
      if (error.response?.status === 403 && error.response?.data?.limitExceeded) {
        setLimitError(error.response.data);
      } else {
        onBack();
      }
    }
  };

  const handleShortlist = async () => {
    try {
      const response = await api.post("/shortlist", { profileId: profile._id });
      setIsShortlisted(!isShortlisted);
    } catch (error) {
      console.error("Error shortlisting:", error);
    }
  };

  const handleRevealContact = async () => {
    setLoading(true);
    setContactError("");

    try {
      const response = await api.post("/reveal-contact", {
        profileId: profile._id,
      });

      if (response.data.success) {
        setContactInfo({
          mobile: response.data.mobile,
          email: response.data.email,
        });
        setContactRevealed(true);
      }
    } catch (error) {
      // Read backend error message safely
      const message =
        error.response?.data?.message ||
        "Unable to reveal contact details. Please try again.";

      setContactError(message);
    } finally {
      setLoading(false);
    }
  };

  if (limitError) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center px-4">
        <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass max-w-md w-full rounded-2xl shadow-xl p-6 text-center animate-fade-in">

          <div className="mx-auto mb-4 w-14 h-14 flex items-center justify-center rounded-full bg-[rgba(255,46,136,0.15)]">
            <FaLock className="text-primary-pink text-2xl" />
          </div>

          <h2 className="text-xl font-bold text-text-primary drop-shadow-sm mb-2">
            Daily View Limit Reached
          </h2>

          <p className="text-text-secondary text-sm mb-6">
            {limitError.message ||
              "You’ve reached your daily profile view limit. Upgrade to continue viewing more profiles."}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={onUpgradeClick}
              className="w-full py-3 bg-primary-pink text-white font-semibold rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition"
            >
              Upgrade Now
            </button>

            <button
              onClick={onBack}
              className="w-full py-2 text-text-secondary hover:text-text-primary drop-shadow-sm text-sm"
            >
              Go Back to Matches
            </button>
          </div>
        </div>
      </div>
    );
  }


  if (!profile) return null;

  const calculateExactAge = (dobString) => {
    if (!dobString) return 'N/A';
    const dob = new Date(dobString);
    const today = new Date();
    let ageVal = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      ageVal--;
    }
    return ageVal;
  };
  const age = calculateExactAge(profile.basicInfo?.dob);

  if (loadingProfile) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rose-600"></div>
      </div>
    );
  }


  const BlurWrapper = ({ locked, children }) => (
    <div className="relative">
      <div className={locked ? "filter blur-md select-none pointer-events-none" : ""}>
        {children}
      </div>

      {locked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <FaLock className="text-white text-3xl bg-black/60 p-3 rounded-full" />
        </div>
      )}
    </div>
  );



  const LockedField = ({ label, icon, value, isLocked }) => (
    <div className="flex items-start gap-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
      <div className="text-text-muted mt-1">{icon}</div>

      <div className="flex-1">
        <div className="text-sm text-text-secondary font-medium mb-1">
          {label}
        </div>

        <div
          className={`font-medium ${isLocked ? "filter blur-sm select-none text-text-primary drop-shadow-sm" : "text-text-primary drop-shadow-sm"
            }`}
        >
          {isLocked ? "Hidden Information" : value}
        </div>
      </div>
    </div>
  );



  return (
    <motion.div
      ref={profileRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-full mx-auto px-4 py-6"
    >


      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-text-secondary hover:text-primary-pink font-medium transition-colors"
        >
          <FaArrowLeft className="mr-2" /> Back to Matches
        </button>

        <div className="flex items-center gap-4">
          <span className="text-sm text-text-muted">
            Profile ID: {profile._id?.slice(-8).toUpperCase()}
          </span>
          {profile.isVerified && (
            <span className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
              <FaCheckCircle /> Verified
            </span>
          )}
        </div>
      </div>

      {/* Main Profile Card */}
      <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border mb-8">
        {/* Profile Header */}
        <div className="p-6 border-b border-glass-border">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Photo */}
            <div className="relative">
              <div className="relative w-40 h-40 rounded-lg overflow-hidden border-4 border-white shadow-lg">
                <img
                  src={
                    profile.profilePhoto
                      ? profile.profilePhoto.startsWith("http") ||
                        profile.profilePhoto.startsWith("blob:") ||
                        profile.profilePhoto.startsWith("data:")
                        ? profile.profilePhoto
                        : `${baseUrl}${profile.profilePhoto}`
                      : profile.basicInfo?.gender === "Female"
                        ? "https://randomuser.me/api/portraits/women/85.jpg"
                        : "https://randomuser.me/api/portraits/men/85.jpg"
                  }
                  alt={profile.basicInfo?.firstName || "Profile"}
                  className="w-full h-full object-cover"
                  draggable="false"
                  onError={(e) => {
                    e.currentTarget.src =
                      profile.basicInfo?.gender === "Female"
                        ? "https://randomuser.me/api/portraits/women/85.jpg"
                        : "https://randomuser.me/api/portraits/men/85.jpg";
                  }}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />


                {/* Subtle diagonal center watermark - much more professional */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-white text-xl font-semibold tracking-widest opacity-30 rotate-[-30deg] select-none">
                    Kalyana Matrimony
                  </span>
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row justify-between items-start mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary drop-shadow-sm mb-1">
                    {profile.basicInfo?.firstName} {profile.basicInfo?.lastName}
                  </h1>
                  <div className="flex items-center gap-4 text-text-secondary mb-3">
                    <span className="flex items-center gap-1">
                      <FaBirthdayCake /> {age} years
                    </span>
                    <span className="flex items-center gap-1">
                      <FaMapMarkerAlt /> {profile.location?.city},{" "}
                      {profile.location?.state}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-[rgba(255,46,136,0.1)] text-primary-pink rounded-full text-sm flex items-center gap-1">
                      <FaVenusMars /> {profile.basicInfo?.gender}
                    </span>
                    <span className="px-3 py-1 bg-[rgba(59,130,246,0.1)] text-blue-700 rounded-full text-sm flex items-center gap-1">
                      <FaGraduationCap /> {profile.educationCareer?.education}
                    </span>
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm flex items-center gap-1">
                      <FaBriefcase /> {profile.educationCareer?.occupation}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-4 md:mt-0">
                  <button
                    onClick={handleShortlist}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isShortlisted
                      ? "bg-primary-pink text-white hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)]"
                      : "bg-[rgba(255,255,255,0.05)] text-text-secondary hover:bg-[rgba(255,255,255,0.08)]"
                      }`}
                  >
                    {isShortlisted ? (
                      <FaHeart className="text-red-400" />
                    ) : (
                      <FaRegHeart />
                    )}
                    {isShortlisted ? "Shortlisted" : "Shortlist"}
                  </button>
                  <button
                    onClick={async () => {
                      if (!planFeatures.chatAccess) {
                        onUpgradeClick?.();
                        return;
                      }


                      if (sendingInterest) return;
                      setSendingInterest(true);
                      try {
                        const targetUserId =
                          typeof profile.userId === "object"
                            ? profile.userId._id
                            : profile.userId;

                        const res = await api.post("/chat/interest/send", {
                          targetUserId,
                        });

                        const conv = res.data.conversation || res.data;
                        onOpenConversation?.(conv?._id || conv?.id || null);
                      } catch (error) {
                        console.error("Error sending interest:", error);
                      } finally {
                        setSendingInterest(false);
                      }
                    }}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium
${!planFeatures.chatAccess
                        ? "bg-gray-600 text-white hover:bg-gray-700"
                        : "bg-primary-pink text-white hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)]"
                      }`}

                  >
                    {!planFeatures.chatAccess ? <FaLock /> : <FaHeart />}
                    {!planFeatures.chatAccess
                      ? "Upgrade to Connect"
                      : sendingInterest
                        ? "Sending..."
                        : "Send Interest"}

                  </button>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-6">
          <StatCard
            icon={<FaPrayingHands className="text-purple-600" />}
            label="Religion"
            value={profile.religionCommunity?.religion || "N/A"}
          />

          <StatCard
            icon={<FaRulerVertical className="text-green-600" />}
            label="Height"
            value={profile.basicInfo?.heightFeet || "N/A"}
          />
          <StatCard
            icon={<FaWeight className="text-orange-600" />}
            label="Weight"
            value={profile.basicInfo?.weight || "N/A"}
          />
        </div>
      </div>

      {/* Profile Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Details Card */}
          <DetailCard
            title="Personal Details"
            icon={<FaUserCheck className="text-primary-pink" />}
            items={[
              {
                icon: <FaBirthdayCake />,
                label: "Date of Birth",
                value: profile.basicInfo?.dob
                  ? new Date(profile.basicInfo.dob).toLocaleDateString()
                  : "N/A",
              },
              {
                icon: <FaVenusMars />,
                label: "Gender",
                value: profile.basicInfo?.gender,
              },
              {
                icon: <FaCalendarAlt />,
                label: "Marital Status",
                value: profile.basicInfo?.maritalStatus,
              },
              {
                icon: <FaTint />,
                label: "Complexion",
                value: profile.basicInfo?.complexion,
              },
              {
                icon: <FaUserFriends />,
                label: "Body Type",
                value: profile.basicInfo?.bodyType,
              },
            ]}
          />

          {/* Religious & Horoscope Details */}
          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-[rgba(255,255,255,0.05)] rounded-lg">
                <FaPrayingHands className="text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-text-primary drop-shadow-sm">
                Religion & Horoscope
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Religion */}
              {profile.religionCommunity?.religion && (
                <div className="flex items-start gap-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                  <FaPrayingHands className="text-text-muted mt-1" />
                  <div>
                    <div className="text-sm text-text-secondary font-medium mb-1">Religion</div>
                    <div className="font-medium text-text-primary drop-shadow-sm">
                      {profile.religionCommunity.religion}
                    </div>
                  </div>
                </div>
              )}

              {/* Caste */}
              {profile.religionCommunity?.caste && (
                <div className="flex items-start gap-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                  <FaIdCard className="text-text-muted mt-1" />
                  <div>
                    <div className="text-sm text-text-secondary font-medium mb-1">Caste</div>
                    <div className="font-medium text-text-primary drop-shadow-sm">
                      {profile.religionCommunity.caste}
                    </div>
                  </div>
                </div>
              )}

              {/* 🔮 Horoscope Fields (LOCKABLE) */}
              <LockedField
                label="Star"
                icon={<FaStar />}
                value={profile.horoscope?.star}
                isLocked={!planFeatures?.horoscopeMatching}
              />

              <LockedField
                label="Rasi"
                icon={<FaStar />}
                value={profile.horoscope?.rasi}
                isLocked={!planFeatures?.horoscopeMatching}
              />

              <LockedField
                label="Dosham"
                icon={<FaShieldAlt />}
                value={profile.horoscope?.dosham}
                isLocked={!planFeatures?.horoscopeMatching}
              />
              {!planFeatures?.horoscopeMatching && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-6 w-full py-2 bg-primary-pink text-white rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] flex items-center justify-center gap-2"
                >
                  <FaLock />
                  Upgrade to Unlock Horoscope
                </button>
              )}


            </div>
          </div>
          {/* Education & Career */}
          <DetailCard
            title="Education & Career"
            icon={<FaGraduationCap className="text-blue-400" />}
            items={[
              {
                icon: <FaGraduationCap />,
                label: "Education",
                value: profile.educationCareer?.education,
              },
              {
                icon: <FaUniversity />,
                label: "College/University",
                value:
                  profile.educationCareer?.college ||
                  profile.educationCareer?.university,
              },
              {
                icon: <FaBriefcase />,
                label: "Occupation",
                value: profile.educationCareer?.occupation,
              },

              {
                icon: <FaMoneyBillWave />,
                label: "Annual Income",
                value: profile.educationCareer?.income,
              },
              {
                icon: <FaCity />,
                label: "Company",
                value: profile.educationCareer?.company,
              },
            ]}
          />
          {profile.jathagamPhoto && (
            <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6">
              <h3 className="text-lg font-bold text-text-primary drop-shadow-sm mb-4 flex items-center gap-2">
                <FaStar className="text-purple-600" />
                Jathagam
              </h3>

              <BlurWrapper locked={!planFeatures?.jathagam}>
                <img
                  src={
                    profile.jathagamPhoto.startsWith("data:")
                      ? profile.jathagamPhoto
                      : `${baseUrl}${profile.jathagamPhoto}`
                  }
                  alt="Jathagam"
                  className="w-full max-h-[400px] object-contain rounded-lg border"
                  draggable="false"
                />
              </BlurWrapper>

              {!planFeatures?.jathagam && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-4 w-full py-2 bg-primary-pink text-white rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)]"
                >
                  Upgrade to View Jathagam
                </button>
              )}
            </div>
          )}
          {profile.photos?.length > 0 && (
            <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6 mt-6">
              <h3 className="text-lg font-bold text-text-primary drop-shadow-sm mb-4 flex items-center gap-2">
                <FaCamera className="text-blue-400" />
                Photos
              </h3>

              <BlurWrapper locked={!planFeatures?.profileViews}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {profile.photos.map((photo, index) => (
                    <img
                      key={index}
                      onClick={() => {
                        if (planFeatures?.profileViews) {
                          setSelectedPhoto(
                            photo.startsWith("data:")
                              ? photo
                              : photo.startsWith("http")
                                ? photo
                                : `${baseUrl}${photo}`
                          );
                        }
                      }}
                      src={
                        photo.startsWith("data:")
                          ? photo
                          : photo.startsWith("http")
                            ? photo
                            : `${baseUrl}${photo}`
                      }
                      className={`w-full h-32 object-contain bg-black/5 rounded-lg border ${planFeatures?.profileViews ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
                      draggable="false"
                      alt={`Photo ${index + 1}`}
                    />
                  ))}
                </div>
              </BlurWrapper>

              {!planFeatures?.profileViews && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-4 w-full py-2 bg-primary-pink text-white rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)]"
                >
                  Upgrade to View Photos
                </button>
              )}
            </div>
          )}




          {/* Family Details */}
          <DetailCard
            title="Family Details"
            icon={<FaHome className="text-green-600" />}
            items={[
              {
                icon: <FaUserFriends />,
                label: "Father's Occupation",
                value: profile.family?.fatherOccupation,
              },
              {
                icon: <FaUserFriends />,
                label: "Mother's Occupation",
                value: profile.family?.motherOccupation,
              },
              {
                icon: <FaUsers />,
                label: "Brothers",
                value: profile.family?.noOfBrothers,
              },
              {
                icon: <FaUsers />,
                label: "Sisters",
                value: profile.family?.noOfSisters,
              },
              {
                icon: <FaHome />,
                label: "Family Type",
                value: profile.family?.familyType,
              },
              {
                icon: <FaMoneyBillWave />,
                label: "Family Value",
                value: profile.family?.familyValues,
              },
            ]}
          />
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6">
            <h3 className="text-lg font-bold text-text-primary drop-shadow-sm mb-4 flex items-center gap-2">
              <FaPhoneAlt className="text-primary-pink" />
              Contact Information
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                <div className="flex items-center gap-3 mb-1">
                  <FaPhoneAlt className="text-text-muted" />
                  <span className="text-sm font-medium text-text-secondary">
                    Mobile Number
                  </span>
                </div>
                <div
                  className={`font-medium text-text-primary drop-shadow-sm ${!contactRevealed ? "filter blur-md select-none" : ""
                    }`}
                >
                  {contactRevealed ? contactInfo?.mobile : "XXXXX XXXXX"}
                </div>
              </div>

              <div className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                <div className="flex items-center gap-3 mb-1">
                  <FaEnvelope className="text-text-muted" />
                  <span className="text-sm font-medium text-text-secondary">
                    Email Address
                  </span>
                </div>
                <div
                  className={`font-medium text-text-primary drop-shadow-sm ${!contactRevealed ? "filter blur-md select-none" : ""
                    }`}
                >
                  {contactRevealed ? contactInfo?.email : "xxxx@xxxx.com"}
                </div>
              </div>

              {!contactRevealed && (
                <button
                  onClick={handleRevealContact}
                  disabled={loading}
                  className="w-full py-3 bg-primary-pink text-white font-medium rounded-lg hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition-colors flex items-center justify-center gap-2 mt-4"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaEye /> Reveal Contact Details
                    </>
                  )}
                </button>
              )}
              {contactError && (
                <div className="mt-3 text-sm text-red-600 font-medium flex items-start gap-2">
                  <span>⚠</span>
                  <span>{contactError}</span>
                </div>
              )}
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6">
            <h3 className="text-lg font-bold text-text-primary drop-shadow-sm mb-4 flex items-center gap-2">
              <FaMapMarkerAlt className="text-blue-400" />
              Location
            </h3>
            <div className="space-y-3">
              <InfoRow
                icon={<FaCity />}
                label="City"
                value={profile.location?.currentCity}
              />
              <InfoRow
                icon={<FaMapMarkerAlt />}
                label="State"
                value={profile.location?.state}
              />
              <InfoRow
                icon={<FaGlobe />}
                label="Country"
                value={profile.location?.country}
              />
              <InfoRow
                icon={<FaHome />}
                label="Native Place"
                value={profile.location?.familyOrigin || "Not specified"}
              />
            </div>
          </div>

          {/* Lifestyle */}
          <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6">
            <h3 className="text-lg font-bold text-text-primary drop-shadow-sm mb-4 flex items-center gap-2">
              <FaInfoCircle className="text-green-600" />
              Lifestyle
            </h3>
            <div className="space-y-3">
              <InfoRow
                icon={<FaUtensils />}
                label="Diet"
                value={profile.lifestyle?.diet || "Not specified"}
              />
              <InfoRow
                icon={<FaSmoking />}
                label="Smoking"
                value={profile.lifestyle?.smoking || "Not specified"}
              />
              <InfoRow
                icon={<FaWineGlassAlt />}
                label="Drinking"
                value={profile.lifestyle?.drinking || "Not specified"}
              />
            </div>
          </div>

        </div>
      </div>

      {/* Full-Screen Photo Viewer */}
      {selectedPhoto && createPortal(
        <div 
          className="fixed inset-0 z-[99999] bg-black bg-opacity-80 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedPhoto} 
              alt="Full view" 
              className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-xl"
            />
            <button 
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-colors flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhoto(null);
              }}
            >
              <FaTimes />
            </button>
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

// Reusable Components
const StatCard = ({ icon, label, value }) => (
  <div className="bg-[rgba(255,255,255,0.03)] rounded-lg p-4 text-center">
    <div className="flex justify-center text-2xl mb-2">{icon}</div>
    <div className="text-sm text-text-secondary font-medium mb-1">{label}</div>
    <div className="font-bold text-text-primary drop-shadow-sm">{value}</div>
  </div>
);

const DetailCard = ({ title, icon, items }) => {
  // Remove items with empty / null / undefined values
  const filteredItems = items.filter(
    (item) =>
      item.value !== undefined &&
      item.value !== null &&
      item.value !== "" &&
      item.value !== "Not Specified"
  );

  // If no valid items, don't render the card at all
  if (filteredItems.length === 0) return null;

  return (
    <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-xl shadow-md border border-glass-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-[rgba(255,255,255,0.05)] rounded-lg">{icon}</div>
        <h3 className="text-xl font-bold text-text-primary drop-shadow-sm">{title}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredItems.map((item, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg"
          >
            <div className="text-text-muted mt-1">{item.icon}</div>
            <div>
              <div className="text-sm text-text-secondary font-medium mb-1">
                {item.label}
              </div>
              <div className="font-medium text-text-primary drop-shadow-sm">
                {item.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-glass-border last:border-b-0">
    <div className="flex items-center gap-2 text-text-secondary">
      {icon && <span className="text-text-muted">{icon}</span>}
      <span className="font-medium">{label}</span>
    </div>
    <span className="font-medium text-text-primary drop-shadow-sm">{value}</span>
  </div>
);

export default ProfileDetails;
